import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';

/**
 * Auth/session data access. Ports the OG's `_loadProfileForAuthUser`,
 * `_autoJoinWinrOfficial`, and `_isWinrOwnerIdentity` (js/supabase.js) so the
 * V2 session carries the same `profiles` row + side effects as V1.
 */

/**
 * Load the `profiles` row for an authenticated user. The `on_auth_user_created`
 * trigger inserts the row, but on first sign-in there's a race — retry once.
 * Mirrors OG `_loadProfileForAuthUser` (js/supabase.js:185).
 */
export async function loadProfile(userId: string): Promise<Profile | null> {
  let { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (!data) {
    await new Promise((r) => setTimeout(r, 400));
    ({ data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle());
  }

  if (error) {
    console.error('profiles fetch failed', error);
    return null;
  }
  return data;
}

/**
 * Auto-add the signed-in user to the flagship WINR community.
 * Mirrors OG `_autoJoinWinrOfficial` → `db.rpc('auto_join_winr_official')`
 * (js/supabase.js:212).
 */
export async function autoJoinWinrOfficial(): Promise<void> {
  const { error } = await supabase.rpc('auto_join_winr_official');
  if (error) console.error('auto_join_winr_official failed:', error);
}

/** Handles that own THE WINR community. Ported from OG `WINR_OWNER_HANDLES`. */
const WINR_OWNER_HANDLES = new Set(['neotpf', '@neotpf', 'neotpf#0']);

/**
 * Whether the auth user is the WINR owner identity (by Discord handle / email).
 * Mirrors OG `_isWinrOwnerIdentity` (js/supabase.js:27).
 */
export function isWinrOwnerIdentity(authUser: User | null): boolean {
  if (!authUser) return false;
  const meta = (authUser.user_metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    meta.user_name,
    meta.preferred_username,
    meta.username,
    meta.name,
    meta.full_name,
    meta.global_name,
    authUser.email,
    (authUser.email ?? '').split('@')[0],
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase().trim());
  return candidates.some((c) => WINR_OWNER_HANDLES.has(c));
}
